import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { useError } from "../contexts/ErrorContext";
import { getSubscriptionStatus, getDrafts } from "../utils/api";
import TemplateGrid from "./TemplateGrid";
import CardBuilderModal from "./CardBuilderModal";
import TemplatePreviewModal from "./TemplatePreviewModal";
import { TransformSection } from "./TransformSection";
import { PreviewSection } from "./PreviewSection";
import SEO from "./SEO";
import "./Hero.css";

import gen4 from "../assets/Generated_Examples/4.jpeg";
import gen5 from "../assets/Generated_Examples/5.jpeg";
import gen6 from "../assets/Generated_Examples/6.jpeg";
import gen7 from "../assets/Generated_Examples/7.jpeg";
import gen8 from "../assets/Generated_Examples/8.jpeg";
import gen9 from "../assets/Generated_Examples/9.png";

const FLOAT_IMAGES = [gen4, gen5, gen6, gen7, gen8, gen9];

// six nicely spaced slots with only slight overlap
const POSITIONS = [
  { top: "-5%",   left: "0%",   rot: -6, anim: "float1" },
  { top: "-8%",   left: "30%",  rot:  6, anim: "float2" },
  { top: "-5%",   left: "60%",  rot: -4, anim: "float3" },
  { bottom: "-5%", left: "10%", rot:  4, anim: "float2" },
  { bottom: "-8%", left: "40%", rot: -8, anim: "float1" },
  { bottom: "-5%", left: "70%", rot:  5, anim: "float3" },
];

export default function Hero() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthContext();
  const { showError } = useError();
  const [aiOpen, setAiOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null); // For showing template preview modal from URL

  // Scroll to section based on URL hash
  const scrollToSection = useCallback((hash) => {
    if (!hash) return;
    const sectionId = hash.replace('#', '');
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, []);

  // Handle URL hash changes for section navigation
  useEffect(() => {
    if (location.hash) {
      scrollToSection(location.hash);
    }
  }, [location.hash, scrollToSection]);

  // Handle URL parameters for messages and template deep links
  useEffect(() => {
    const urlMessage = searchParams.get("message");
    if (urlMessage) {
      setMessage(urlMessage);
      // Clear the message from the URL but keep hash
      navigate("/" + location.hash, { replace: true });
    }

    // Check for direct template link: ?template=Category/filename or ?category=X&templateId=Y
    const templateParam = searchParams.get("template");
    const category = searchParams.get("category");
    const templateId = searchParams.get("templateId");

    if (templateParam) {
      // Format: ?template=Christmas/christmas1.png
      const [cat, filename] = templateParam.split('/');
      if (cat && filename) {
        // Show preview modal instead of auto-selecting
        setPreviewTemplate({ category: cat, filename, templateId: filename.replace(/[^0-9]/g, '') });
        // Clear URL params
        navigate("/", { replace: true });
      }
    } else if (category && templateId) {
      // Format: ?category=Christmas&templateId=1
      // Show preview modal instead of auto-selecting
      setPreviewTemplate({ category, templateId });
      navigate("/", { replace: true });
    } else if (category && !templateId) {
      // Format: ?category=Christmas (no specific template, just category)
      setPendingCategory(category);
      scrollToSection('#templates');
      navigate("/#templates", { replace: true });
    }
  }, [searchParams, navigate, location.hash, scrollToSection]);

  // Generate a shareable link for a template
  const getTemplateShareLink = useCallback((template) => {
    const baseUrl = window.location.origin;
    const category = template.category || "General";
    const filename = template.filename || `${category.toLowerCase()}${template.id || 1}.png`;
    return `${baseUrl}/?template=${encodeURIComponent(category)}/${encodeURIComponent(filename)}#templates`;
  }, []);

  // Generate a shareable link for a category
  const getCategoryShareLink = useCallback((category) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?category=${encodeURIComponent(category)}#templates`;
  }, []);

  // Handle when user clicks "Use This Template" in preview modal
  const handleUsePreviewTemplate = async (template) => {
    const { category, filename, templateId } = template;
    const baseUrl = process.env.REACT_APP_API_URL || "http://192.168.1.165:8000";
    const file = filename || `${category.toLowerCase()}${templateId || 1}.png`;
    const src = `${baseUrl}/templates/${category}/${file}`;
    
    // Check if this is a video template by file extension
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    const isVideo = videoExtensions.some(ext => file.toLowerCase().endsWith(ext));
    const mediaType = isVideo ? "video" : "image";
    
    // Close the modal
    setPreviewTemplate(null);
    
    // Build full template object and pass to existing handler
    const fullTemplate = {
      id: templateId || filename?.replace(/[^0-9]/g, '') || "1",
      category: category,
      src: src,
      filename: file,
      cardSize: "1024x1024", // Default, will be recalculated in editing
      isVideo: isVideo,
      mediaType: mediaType
    };
    
    // Use existing template selection handler (handles auth flow)
    handleTemplateSelect(fullTemplate);
  };

  const handleTemplateSelect = async (template) => {
    if (!template) return;

    // Clear any pending template once we're processing one
    setPendingTemplate(null);

    // Check if user is authenticated
    if (!user) {
      // Redirect to signup with template context (new users need to create account first)
      const category = template.category || "General";
      const templateId = template.id || "1";
      const redirectPath = `/edit/${category}/${templateId}`;
      
      navigate('/register?redirect=' + encodeURIComponent(redirectPath), { 
        state: { 
          fromTemplate: true, 
          message: 'Create a free account to access our beautiful templates and start customizing!',
          templateData: {
            id: templateId,
            src: template.src,
            category,
            cardSize: template.cardSize || "1024x1024",
            isVideo: template.isVideo || false,
            mediaType: template.mediaType || "image",
            firebaseUrl: template.firebaseUrl || null,
            filename: template.filename
          }
        } 
      });
      return;
    }

    // User is authenticated, check subscription status
    let statusData;
    try {
      const response = await getSubscriptionStatus();
      statusData = response.data;
      
      // Check if user has an active subscription (paid plan required)
      // Users without a subscription must subscribe first
      if (!statusData.active || !statusData.subscription_data) {
        const category = template.category || "General";
        const templateId = template.id || "1";
        navigate('/subscribe', {
          state: {
            fromTemplate: true,
            message: 'Subscribe to access our beautiful templates and start creating!',
            templateData: {
              id: templateId,
              src: template.src,
              category,
              cardSize: template.cardSize || "1024x1024",
              isVideo: template.isVideo || false,
              mediaType: template.mediaType || "image",
              firebaseUrl: template.firebaseUrl || null,
              filename: template.filename
            }
          }
        });
        return;
      }
    } catch (err) {
      console.error("Subscription check failed:", err);
      // If subscription check fails, redirect to subscribe page to be safe
      const category = template.category || "General";
      const templateId = template.id || "1";
      navigate('/subscribe', {
        state: {
          fromTemplate: true,
          message: 'Subscribe to access our beautiful templates and start creating!',
          templateData: {
            id: templateId,
            src: template.src,
            category,
            cardSize: template.cardSize || "1024x1024",
            isVideo: template.isVideo || false,
            mediaType: template.mediaType || "image",
            firebaseUrl: template.firebaseUrl || null,
            filename: template.filename
          }
        }
      });
      return;
    }

    // Check draft limit before proceeding (only for plans with limited drafts)
    const draftLimit = statusData?.limits?.drafts_per_user;
    // Only check if limit is a finite number (not unlimited/-1)
    if (typeof draftLimit === 'number' && draftLimit > 0 && draftLimit < 999999) {
      try {
        const { data: drafts } = await getDrafts();
        const currentDraftCount = Array.isArray(drafts) ? drafts.length : 0;
        
        if (currentDraftCount >= draftLimit) {
          // User has reached their draft limit - show upgrade modal
          showError(
            `You've reached your card limit! Upgrade to create more cards and unlock all features.`,
            true // requiresUpgrade = true to show upgrade modal
          );
          return;
        }
      } catch (err) {
        // If we can't check drafts, log but don't block (let save_draft handle it)
        console.warn("Could not check draft count:", err);
      }
    }

    // User is authenticated and has active subscription, proceed to editing
    const category = template.category || "General";
    const templateId = template.id || "1";
    navigate(`/edit/${category}/${templateId}`, {
      state: {
        src: template.src,
        category,
        cardSize: template.cardSize || "1024x1024",
        mediaType: template.mediaType || "image",  // NEW: Pass media type for video support
        isVideo: template.isVideo || false         // NEW: Convenience flag for video templates
      }
    });
  };

  const handleCreateCard = () => {
    if (!user) {
      // Redirect to signup with appropriate message (new users need to create account first)
      navigate('/register?redirect=' + encodeURIComponent('/'), { 
        state: { 
          fromAI: true, 
          message: 'Create a free account to access AI-powered greeting cards' 
        } 
      });
      return;
    }
    
    // User is authenticated, open the AI modal
    setAiOpen(true);
  };

  return (
    <>
      <SEO
        title={null}
        description="Create beautiful, personalized greeting cards with AI. Design and send custom cards for birthdays, Christmas, holidays, and special occasions. Start with our free trial today!"
        path="/"
      />
      
      {/* Mother's Day promotional banner */}
      <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-400 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <div className="flex items-center gap-2">
            <span className="text-xl">🌷</span>
            <span className="font-semibold">{"Mother's Day is coming!"}</span>
          </div>
          <span className="hidden sm:inline text-violet-100">|</span>
          <span className="text-violet-100 text-sm sm:text-base">Create a beautiful card for Mom</span>
          <button
            onClick={() => {
              const templatesSection = document.getElementById('templates');
              if (templatesSection) {
                templatesSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="px-4 py-1.5 bg-white text-violet-700 rounded-full text-sm font-bold hover:bg-violet-50 transition-colors shadow-lg"
          >
            {"Explore Mother's Day cards →"}
          </button>
        </div>
      </div>
      
      <section id="hero" className="hero-gradient text-white py-12 md:py-32 rounded-b-[50px] relative overflow-hidden">
        {message && (
          <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-black py-3 px-6 text-center">
            {message}
          </div>
        )}
        
        {/* dotted overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1200 800"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <pattern
                id="dots"
                x="0"
                y="0"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between">
          {/* copy + button */}
          <div className="md:w-1/2 text-center md:text-left mb-8 md:mb-0">
            <h1 className="text-3xl md:text-6xl lg:text-7xl font-extrabold mb-4 md:mb-6 leading-tight">
              Gift Unique, One-of-a-Kind Cards
            </h1>
            <p className="text-lg md:text-2xl text-indigo-100 mb-4 md:mb-6 max-w-lg">
              Instantly Create and Send AI-Generated Greeting Cards for Any Occasion
            </p>
            
            {/* Feature highlights */}
            <div className="flex flex-wrap gap-2 mb-6 md:mb-8 justify-center md:justify-start">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm text-indigo-100 border border-white/20">
                <span>🎨</span>
                <span>AI-Powered Design</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm text-indigo-100 border border-white/20">
                <span>✉️</span>
                <span>Instant Delivery</span>
              </span>
            </div>
            
            {/* Main CTA with dual options */}
            <div className="space-y-4">
              <button
                onClick={handleCreateCard}
                className="button-create mx-auto md:mx-0"
              >
                <span className="text-2xl leading-none">＋</span>
                <span>Create with AI</span>
                <span className="badge-ai">AI</span>
              </button>
              
              {/* Template CTA */}
              <div className="text-center md:text-left">
                <p className="text-indigo-200 text-lg mb-2">
                  Or start with a template
                </p>
                <button
                  onClick={() => {
                    const templatesSection = document.getElementById('templates');
                    if (templatesSection) {
                      templatesSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="inline-flex items-center px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-medium transition-all border border-white/20 hover:border-white/30"
                >
                  <span>Browse Templates</span>
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {!user && (
              <p className="mt-4 text-sm text-indigo-200">
                Sign in to unlock AI-powered card creation and premium templates
              </p>
            )}
          </div>

          {/* floating cards - hidden on mobile */}
          <div className="hidden md:block md:w-1/2 relative h-[26rem] overflow-visible">
            {FLOAT_IMAGES.map((src, i) => {
              const { top, bottom, left, right, rot, anim } = POSITIONS[i];
              return (
                <div
                  key={i}
                  className={`floating-card ${anim}`}
                  style={{
                    top:   top    ?? "auto",
                    bottom: bottom ?? "auto",
                    left:  left   ?? "auto",
                    right: right  ?? "auto",
                    "--initial-rot": `${rot}deg`,
                  }}
                >
                  <img
                    src={src}
                    alt={`Example ${i+1}`}
                    className="floating-img"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="transform">
        <TransformSection onCreateClick={handleCreateCard} />
      </section>

      <section id="preview">
        <PreviewSection onCreateClick={handleCreateCard} />
      </section>

      <section id="templates">
        <TemplateGrid 
          onPick={handleTemplateSelect} 
          onCreateWithAI={handleCreateCard}
          pendingTemplate={pendingTemplate}
          pendingCategory={pendingCategory}
          onCategoryChange={() => setPendingCategory(null)}
          getShareLink={getTemplateShareLink}
          getCategoryShareLink={getCategoryShareLink}
        />
      </section>

      {aiOpen && <CardBuilderModal mode="ai" onClose={() => setAiOpen(false)} />}
      
      {/* Template preview modal - shown when arriving via template link */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onUseTemplate={handleUsePreviewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </>
  );
}
